import { getJobTrackerCustomerStatus } from "@/lib/jobTrackerCustomerStatus";
import { APPROVE_REPAIR_LABEL, DECLINE_REPAIR_LABEL } from "../../../shared/repairProposalWorkflow";
import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { useParams } from "wouter";

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function JobTracker() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const utils = trpc.useUtils();

  const { data: job, isLoading, error } = trpc.jobs.getByToken.useQuery(
    { token: token ?? "" },
    { enabled: !!token, retry: false }
  );

  const approveRepair = trpc.jobs.customerApprove.useMutation({
    onSuccess: () => utils.jobs.getByToken.invalidate({ token: token ?? "" }),
  });
  const declineRepair = trpc.jobs.customerDecline.useMutation({
    onSuccess: () => utils.jobs.getByToken.invalidate({ token: token ?? "" }),
  });

  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Loading your repair status…</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold mb-2">Job Not Found</h2>
            <p className="text-sm text-gray-500">
              We could not find a repair order matching this link. Please check the link in your
              notification message or contact us directly.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isDeclined = job.status === "customer_declined";
  const isAwaitingApproval = job.status === "awaiting_customer_confirmation";
  const customerStatus = getJobTrackerCustomerStatus(job.status);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-8 max-w-2xl mx-auto w-full">
        {(job as { proposalSuperseded?: boolean }).proposalSuperseded && (
          <div className="mb-8 border border-amber-300 bg-amber-50 rounded-lg p-5">
            <h2 className="text-lg font-semibold text-amber-950">This proposal has been updated</h2>
            <p className="text-sm text-amber-900 mt-2">
              The repair proposal previously sent to you is no longer available for approval. Please wait for an
              updated proposal from your consultant.
            </p>
          </div>
        )}
        {/* Job reference card */}
        <div className="border border-gray-200 rounded-lg p-5 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Job Reference</p>
              <p className="text-2xl font-bold tracking-wider text-black">{job.jobNumber}</p>
              <p className="mt-1 text-sm font-medium text-gray-800">
                {job.customerName?.trim() || "—"}
              </p>
            </div>
            <StatusPill status={job.status} />
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Equipment</p>
              <p className="font-medium text-gray-800">
                {job.brand} {job.model}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Serial Number</p>
              <p className="font-medium text-gray-800">{job.serialNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Date Received</p>
              <p className="font-medium text-gray-800">{formatDate(job.createdAt)}</p>
            </div>
            {(job as { estimatedRepairDuration?: string | null }).estimatedRepairDuration && (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">
                  Estimated Duration
                </p>
                <p className="font-medium text-gray-800">{(job as any).estimatedRepairDuration}</p>
              </div>
            )}
          </div>
        </div>

        {/* ─── Current status (only — no process rail) ───────────────────────── */}
        <div
          className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6"
          data-testid="job-tracker-current-status"
        >
          <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Current Status</p>
          <p className="font-semibold text-gray-900 mb-1">{customerStatus.title}</p>
          <p className="text-sm text-gray-600">{customerStatus.message}</p>
          {job.status === "closed" && (job as any).closedAt && (
            <p className="text-xs text-gray-400 mt-2">
              Completed on {formatDate((job as any).closedAt)}
            </p>
          )}
        </div>

        {/* ─── Customer Declined State ──────────────────────────────────────── */}
        {isDeclined && (
          <div className="mb-8 border border-red-200 rounded-lg p-5 bg-red-50">
            <div className="flex items-start gap-3">
              <span className="text-2xl">❌</span>
              <div>
                <p className="font-semibold text-red-900 mb-1">Repair Declined</p>
                <p className="text-sm text-red-800">
                  You have declined the repair. Our team will be in touch to discuss next steps,
                  including returning your equipment.
                </p>
                {(job as any).customerDeclinedAt && (
                  <p className="text-xs text-red-600 mt-2">
                    Declined on {formatDate((job as any).customerDeclinedAt)}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-red-200">
              <p className="text-xs text-red-700 mb-3">Changed your mind? Contact us:</p>
              <a
                href="https://wa.me/6565116511"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <WhatsAppIcon />
                WhatsApp Us
              </a>
            </div>
          </div>
        )}

        {/* ─── Awaiting Approval — Quote Card ──────────────────────────────── */}
        {isAwaitingApproval && job.diagnosedFault && (
          <div className="mb-8 border-2 border-orange-300 rounded-lg overflow-hidden">
            <div className="bg-orange-50 px-5 py-4 border-b border-orange-200">
              <p className="text-xs text-orange-600 uppercase tracking-widest font-semibold mb-1">
                Action Required
              </p>
              <p className="font-semibold text-orange-900 text-lg">
                Your Repair Quote is Ready
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Diagnosis</p>
                <p className="text-sm text-gray-800 leading-relaxed">{job.diagnosedFault}</p>
              </div>
              {(job as { proposedRepair?: string | null }).proposedRepair && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Proposed Repair</p>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {(job as { proposedRepair?: string | null }).proposedRepair}
                  </p>
                </div>
              )}
              {(job as { estimatedRepairDuration?: string | null }).estimatedRepairDuration && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estimated Duration</p>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {(job as { estimatedRepairDuration?: string | null }).estimatedRepairDuration}
                  </p>
                </div>
              )}
              {/* Repair price */}
              {job.actualCost && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Repair Price</p>
                  <p className="text-3xl font-bold text-gray-900">
                    S${parseFloat(String(job.actualCost)).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Inclusive of parts and labour</p>
                </div>
              )}

              {/* Approve / Decline */}
              {!approveRepair.isSuccess && !declineRepair.isSuccess && (
                <div className="pt-2 space-y-3">
                  <p className="text-sm text-gray-700">
                    Please review the above and let us know how you would like to proceed:
                  </p>

                  {/* Approve confirm */}
                  {!showApproveConfirm && !showDeclineConfirm && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setShowApproveConfirm(true)}
                        className="flex-1 bg-black text-white py-3 px-6 rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors"
                      >
                        {APPROVE_REPAIR_LABEL}
                      </button>
                      <button
                        onClick={() => setShowDeclineConfirm(true)}
                        className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-colors"
                      >
                        {DECLINE_REPAIR_LABEL}
                      </button>
                    </div>
                  )}

                  {/* Approve confirmation dialog */}
                  {showApproveConfirm && (
                    <div className="border border-black rounded-lg p-4 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-900 mb-2">Confirm Approval</p>
                      <p className="text-sm text-gray-700 mb-4">
                        By approving, you authorise Clarity to proceed with the repair at the quoted
                        cost of <strong>S${parseFloat(String(job.actualCost ?? "0")).toFixed(2)}</strong>.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() =>
                            approveRepair.mutate({
                              token: token ?? "",
                              proposalVersion: (job as { proposalVersion?: number }).proposalVersion,
                            })
                          }
                          disabled={approveRepair.isPending}
                          className="flex-1 bg-black text-white py-2.5 px-4 rounded-lg font-semibold text-sm hover:bg-gray-800 disabled:opacity-50 transition-colors"
                        >
                          {approveRepair.isPending ? "Confirming…" : "Yes, Approve"}
                        </button>
                        <button
                          onClick={() => setShowApproveConfirm(false)}
                          className="flex-1 border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg text-sm hover:bg-white transition-colors"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Decline confirmation dialog */}
                  {showDeclineConfirm && (
                    <div className="border border-red-300 rounded-lg p-4 bg-red-50">
                      <p className="text-sm font-semibold text-red-900 mb-2">Confirm Decline</p>
                      <p className="text-sm text-red-800 mb-4">
                        Are you sure you want to decline the repair? Our team will contact you to
                        arrange return of your equipment.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => declineRepair.mutate({ token: token ?? "", proposalVersion: job.proposalVersion! })}
                          disabled={declineRepair.isPending}
                          className="flex-1 bg-red-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {declineRepair.isPending ? "Declining…" : "Yes, Decline"}
                        </button>
                        <button
                          onClick={() => setShowDeclineConfirm(false)}
                          className="flex-1 border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg text-sm hover:bg-white transition-colors"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Success states */}
              {approveRepair.isSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">✅</p>
                  <p className="font-semibold text-green-900">Repair Approved</p>
                  <p className="text-sm text-green-800 mt-1">
                    Thank you! Our team will begin work on your equipment shortly.
                  </p>
                </div>
              )}
              {declineRepair.isSuccess && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">📞</p>
                  <p className="font-semibold text-gray-900">Decline Recorded</p>
                  <p className="text-sm text-gray-700 mt-1">
                    Our team will be in touch shortly to discuss next steps.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Customer Approved banner ─────────────────────────────────────── */}
        {(job as any).customerApprovedAt && job.status !== "awaiting_customer_confirmation" && (
          <div className="mb-6 border border-green-200 rounded-lg p-4 bg-green-50 flex items-center gap-3">
            <span className="text-xl">✅</span>
            <div>
              <p className="text-sm font-semibold text-green-900">You approved this repair</p>
              <p className="text-xs text-green-700 mt-0.5">
                Approved on {formatDate((job as any).customerApprovedAt)}
              </p>
            </div>
          </div>
        )}

        {/* ─── Contact Us ──────────────────────────────────────────────────── */}
        {!isDeclined && (
          <div className="border border-gray-200 rounded-lg p-5">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">Need Help?</p>
            <p className="text-sm text-gray-600 mb-4">
              Have a question about your repair? Contact our service team directly.
            </p>
            <a
              href={`https://wa.me/6565116511?text=Hi%2C%20I%20have%20a%20question%20about%20my%20repair%20job%20${encodeURIComponent(job.jobNumber)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <WhatsAppIcon />
              WhatsApp Us
            </a>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function Header() {
  return (
    <header className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
      <span className="text-xs text-gray-400 uppercase tracking-widest">Repair Status</span>
      <img
        src="https://d2xsxph8kpxj0f.cloudfront.net/310519663414100320/brqZLwPGMDsYTB8zGXzCpe/clarity-logo_db6df1c5.jpg"
        alt="Clarity"
        className="h-7 object-contain"
      />
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-100 px-6 py-5 text-center">
      <p className="text-xs text-gray-400">
        © Clarity Group Pte Ltd · Please quote your job reference number in all correspondence.
      </p>
    </footer>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    received: "bg-blue-50 text-blue-700 border-blue-200",
    diagnosis: "bg-yellow-50 text-yellow-700 border-yellow-200",
    awaiting_customer_confirmation: "bg-orange-50 text-orange-700 border-orange-200",
    customer_declined: "bg-red-50 text-red-700 border-red-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    pending_parts: "bg-orange-50 text-orange-700 border-orange-200",
    repair_complete: "bg-green-50 text-green-700 border-green-200",
    awaiting_payment: "bg-pink-50 text-pink-700 border-pink-200",
    awaiting_collection: "bg-purple-50 text-purple-700 border-purple-200",
    closed: "bg-gray-50 text-gray-600 border-gray-200",
  };
  const { title } = getJobTrackerCustomerStatus(status);
  const style = styles[status] || "bg-gray-50 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${style}`}
      data-testid="job-tracker-status-pill"
    >
      {title}
    </span>
  );
}
